/**
 * The help assistant's allowed pages and tours, as pure functions (D10-ACT,
 * D10-SEC-003, #3624).
 *
 * One definition serves both sides of the client/server contract:
 *  - `useSupportLists` sends these lists with every chat turn;
 *  - `SupportKbCatalog.test.ts` exports them per persona into the backend
 *    knowledge pack's catalogue file, where a backend test replays each
 *    persona's lists through the chat endpoint and fails if the server drops
 *    or flags a single value. The catalogue and the lists can therefore never
 *    drift apart unnoticed again.
 *
 * Routes are the user's navigation commands (the sidebar's own visibility
 * predicate) plus their landing page, which is always theirs to open even when
 * their menu hides it (the office admin lands on the dashboard) or no menu has
 * it at all (`/finance`, `/accounts`: `SUPPORT_HOME_PAGES`). Tours are every
 * registered tour the engine would offer this user.
 */
import { adminNavGroups } from "../../components/layout/adminNav";
import { navGroups } from "../../components/layout/crmNav";
import type { CommandEntry } from "../commands/types";
import { TOUR_REGISTRY } from "../tours/registry";
import { isTourAvailable } from "../tours/filter";
import type { TourGateContext } from "../tours/types";

export interface SupportHomePage {
  route: string;
  label: string;
  purpose: string;
}

/**
 * Persona landing pages that are not in any nav group (the nav links the
 * combined Finance/Accounts workspace instead), so the knowledge pack still
 * knows them: a user sitting on their own home page is never "off the map".
 * Keep in step with `landingPathForUser` (ProtectedRoute.tsx).
 */
export const SUPPORT_HOME_PAGES: readonly SupportHomePage[] = [
  {
    route: "/finance",
    label: "Finance",
    purpose: "Authorised commission splits, commission runs and deposit interest",
  },
  {
    route: "/accounts",
    label: "Deposits to Invest",
    purpose: "Deposits waiting to be placed on investment",
  },
];

/** Every page the knowledge-pack catalogue lists: all nav items plus the homes. */
const CATALOGUED_ROUTES = new Set<string>([
  ...adminNavGroups.flatMap((g) => g.items.map((i) => i.to)),
  ...navGroups.flatMap((g) => g.items.map((i) => i.to)),
  ...SUPPORT_HOME_PAGES.map((p) => p.route),
]);

/** The nav routes plus the user's landing page (when the catalogue lists it). */
export function supportAllowedRoutes(
  commands: readonly CommandEntry[],
  landingPath: string | null | undefined,
): string[] {
  const routes = Array.from(
    new Set(commands.flatMap((c) => (c.kind === "nav" ? [c.url] : []))),
  );
  if (landingPath && CATALOGUED_ROUTES.has(landingPath) && !routes.includes(landingPath)) {
    routes.push(landingPath);
  }
  return routes;
}

/** Every registered tour the engine would offer under this gate. */
export function supportAllowedTours(gate: TourGateContext): string[] {
  return Object.values(TOUR_REGISTRY)
    .filter((t) => isTourAvailable(t, gate))
    .map((t) => t.id);
}
